import { useEffect, useState } from 'react'
import { Button, Radio, Spin, Table, Tabs } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import { getPortfolios } from '@/api/portfolio'
import { useFeedback } from '@/utils/antdApp'
import type { ManagerPortfolio, Portfolio } from '@/types/portfolio'
import './index.scss'

const statusMap: Record<number, { label: string; cls: string }> = {
    1: { label: '运营中',  cls: 'active' },
    2: { label: '清仓中',  cls: 'liquidating' },
    3: { label: '观察期',  cls: 'watching' },
    4: { label: '已停盘',  cls: 'archived' },
}

const statusTabs = [
    { key: 'all',     label: '全部' },
    { key: '1',       label: '运营中' },
    { key: '3',       label: '观察期' },
    { key: '2',       label: '清仓中' },
    { key: '4',       label: '已停盘' },
    { key: 'pending', label: '审核中' },
]

function fmtWan(val: number | null | undefined) {
    const v = val ?? 0
    if (Math.abs(v) >= 10000) return (v / 10000).toFixed(2) + '亿'
    return v.toFixed(2) + '万'
}

/** 带正负号的万/亿格式，亏损显示为负数 */
function fmtSignedWan(val: number | null | undefined) {
    const v = val ?? 0
    if (v === 0) return fmtWan(0)
    return `${v > 0 ? '+' : '-'}${fmtWan(Math.abs(v))}`
}

function fmtPct(val: number | null | undefined) {
    if (val == null) return '--'
    return `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`
}

function retClass(val: number | null | undefined) {
    if (val == null) return ''
    return val >= 0 ? 'pos' : 'neg'
}

function SummaryBar({ managers }: { managers: ManagerPortfolio[] }) {
    const all = managers.flatMap(m => m.portfolios)
    const live = all.filter(p => p.status === 1 || p.status === 3)
    const totalAUM = live.reduce((s, p) => s + (p.current_nav ?? 0), 0)
    const todayPnl = live.reduce((s, p) => s + (p.today_pnl_wan ?? 0), 0)
    const activeCount = all.filter(p => p.status === 1).length
    const watchingCount = all.filter(p => p.status === 3).length

    return (
        <div className="asset-mgmt__summary-bar">
            <div className="asset-mgmt__sum-item">
                <div className="asset-mgmt__sum-val">{fmtWan(totalAUM)}</div>
                <div className="asset-mgmt__sum-lbl">总 AUM</div>
            </div>
            <div className="asset-mgmt__sum-divider" />
            <div className="asset-mgmt__sum-item">
                <div className={`asset-mgmt__sum-val ${retClass(todayPnl)}`}>
                    {fmtSignedWan(todayPnl)}
                </div>
                <div className="asset-mgmt__sum-lbl">今日 P&L</div>
            </div>
            <div className="asset-mgmt__sum-divider" />
            <div className="asset-mgmt__sum-item">
                <div className="asset-mgmt__sum-val">
                    {activeCount} 运营 · {watchingCount} 观察
                </div>
                <div className="asset-mgmt__sum-lbl">组合状态</div>
            </div>
            <div className="asset-mgmt__sum-divider" />
            <div className="asset-mgmt__sum-item">
                <div className="asset-mgmt__sum-val">{all.length}</div>
                <div className="asset-mgmt__sum-lbl">总组合数</div>
            </div>
        </div>
    )
}

function PortfolioCard({ p, onDetail }: { p: Portfolio; onDetail: (id: number) => void }) {
    const st = statusMap[p.status] ?? { label: String(p.status), cls: '' }
    const pnl = p.profit_wan ?? 0
    const todayPnl = p.today_pnl_wan ?? 0

    return (
        <div className="asset-mgmt__card" onClick={() => onDetail(p.id)}>
            <div className="asset-mgmt__card-header">
                <span className="asset-mgmt__card-name">{p.name}</span>
                <span className={`asset-mgmt__status-tag asset-mgmt__status-tag--${st.cls}`}>{st.label}</span>
                <span className="asset-mgmt__market-tag">{p.market}</span>
            </div>
            <div className="asset-mgmt__card-desc">{p.description || p.portfolio}</div>
            <div className="asset-mgmt__card-assets">
                <div className="asset-mgmt__asset-hero">
                    <div className="asset-mgmt__asset-hero-val">{fmtWan(p.current_nav)}</div>
                    <div className="asset-mgmt__asset-hero-lbl">当前总资产</div>
                </div>
                <div className="asset-mgmt__asset-sub">
                    <div className="asset-mgmt__asset-sub-item">
                        <div className="asset-mgmt__asset-sub-lbl">初始资金</div>
                        <div className="asset-mgmt__asset-sub-val">{fmtWan(p.initial_nav)}</div>
                    </div>
                    <div className="asset-mgmt__asset-sub-item">
                        <div className="asset-mgmt__asset-sub-lbl">盈亏金额</div>
                        <div className={`asset-mgmt__asset-sub-val ${retClass(pnl)}`}>
                            {fmtSignedWan(pnl)}
                        </div>
                    </div>
                </div>
            </div>
            <div className="asset-mgmt__metrics">
                {[
                    {
                        val: p.daily_return_pct,
                        lbl: '今日',
                        text: undefined as string | undefined,
                        sub: fmtSignedWan(todayPnl),
                        subClass: retClass(todayPnl),
                    },
                    { val: p.cumulative_return_pct,  lbl: '累计' },
                    { val: -(p.max_drawdown_pct ?? 0), lbl: '最大回撤' },
                    { val: null, lbl: '夏普', text: String(p.sharpe_ratio ?? '--') },
                ].map(({ val, lbl, text, sub, subClass }) => (
                    <div key={lbl} className="asset-mgmt__metric">
                        <div className={`asset-mgmt__metric-val ${retClass(val)}`}>
                            {text ?? fmtPct(val)}
                        </div>
                        {sub ? (
                            <div className={`asset-mgmt__metric-sub ${subClass || ''}`} style={{ fontSize: 11 }}>
                                {sub}
                            </div>
                        ) : null}
                        <div className="asset-mgmt__metric-lbl">{lbl}</div>
                    </div>
                ))}
            </div>
            <div className="asset-mgmt__card-footer">
                <span className="asset-mgmt__card-days">运行 {p.days ?? 0} 天</span>
                <button className="asset-mgmt__card-btn"
                    onClick={e => { e.stopPropagation(); onDetail(p.id) }}>
                    查看详情 →
                </button>
            </div>
        </div>
    )
}

export default function AssetManagementPage() {
    const navigate = useNavigate()
    const { message } = useFeedback()
    const [managers, setManagers] = useState<ManagerPortfolio[]>([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState('all')
    const [viewMode, setViewMode] = useState<1 | 2>(1)

    useEffect(() => {
        getPortfolios()
            .then(data => setManagers(data ?? []))
            .catch(err => message.error(err instanceof Error ? err.message : '加载组合列表失败'))
            .finally(() => setLoading(false))
    }, [])

    const filteredByStatus = (portfolios: Portfolio[]) => {
        if (statusFilter === 'all') return portfolios
        if (statusFilter === 'pending') return portfolios.filter(p => p.review_pending)
        return portfolios.filter(p => String(p.status) === statusFilter)
    }

    const columns: ColumnsType<Portfolio> = [
        {
            title: '组合', dataIndex: 'name', key: 'name',
            render: (name: string, r: Portfolio) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.market}</div>
                </div>
            ),
        },
        {
            title: '状态', dataIndex: 'status', key: 'status',
            render: (s: number) => {
                const st = statusMap[s]
                return <span className={`asset-mgmt__status-tag asset-mgmt__status-tag--${st?.cls}`}>{st?.label}</span>
            },
        },
        { title: 'AUM（万）', dataIndex: 'current_nav', key: 'aum', render: (v: number) => fmtWan(v) },
        {
            title: '今日盈亏', dataIndex: 'today_pnl_wan', key: 'td_pnl',
            render: (v: number) => (
                <span className={retClass(v)}>
                    {v == null ? '--' : fmtSignedWan(v)}
                </span>
            ),
        },
        {
            title: '今日', dataIndex: 'daily_return_pct', key: '1d',
            render: (v: number) => <span className={retClass(v)}>{fmtPct(v)}</span>,
        },
        {
            title: '累计', dataIndex: 'cumulative_return_pct', key: 'cum',
            render: (v: number) => <span className={retClass(v)}>{fmtPct(v)}</span>,
        },
        {
            title: '最大回撤', dataIndex: 'max_drawdown_pct', key: 'dd',
            render: (v: number) => <span className={v > 10 ? 'neg' : ''}>{v ? `-${v.toFixed(2)}%` : '--'}</span>,
        },
        { title: '夏普', dataIndex: 'sharpe_ratio', key: 'sharpe',
            render: (v: number) => v ? v.toFixed(2) : '--' },
        { title: '运行天数', dataIndex: 'days', key: 'days' },
        {
            title: '', key: 'action',
            render: (_: unknown, r: Portfolio) => (
                <Button type="link" size="small" onClick={() => navigate(`/asset-mgmt/${r.id}`)}>详情 →</Button>
            ),
        },
    ]

    if (loading) {
        return (
            <div className="asset-mgmt">
                <div className="page-header flex flex-between asset-mgmt__header">
                    <span className="color-white font-weight-700">资产管理</span>
                </div>
                <div className="page-content" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
                    <Spin size="large" />
                </div>
            </div>
        )
    }

    return (
        <div className="asset-mgmt">
            <div className="page-header flex flex-between asset-mgmt__header">
                <span className="color-white font-weight-700">资产管理</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Radio.Group
                        className="portfolio-page-header__radio-group"
                        options={['卡片', '表格'].map((el, i) => ({ label: el, value: i + 1 }))}
                        value={viewMode}
                        onChange={e => setViewMode(Number(e.target.value) as 1 | 2)}
                        optionType="button"
                        buttonStyle="solid"
                    />
                    <Button type="primary" onClick={() => navigate('/asset-mgmt/create')}>
                        + 新建组合
                    </Button>
                </div>
            </div>

            <div className="page-content asset-mgmt__content">
                <SummaryBar managers={managers} />

                <Tabs
                    activeKey={statusFilter}
                    onChange={setStatusFilter}
                    items={statusTabs}
                    style={{ marginBottom: 8 }}
                />

                {managers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-3)' }}>
                        暂无组合，点击「新建组合」开始创建
                    </div>
                ) : (
                    managers.map(manager => {
                        const group = filteredByStatus(manager.portfolios)
                        if (group.length === 0) return null

                        const groupTdPl = group.reduce((s, p) => s + (p.today_pnl_wan ?? 0), 0)
                        const withReturn = group.filter(p => p.daily_return_pct != null)
                        const avg = (key: keyof Portfolio) =>
                            withReturn.length
                                ? withReturn.reduce((s, p) => s + ((p[key] as number) ?? 0), 0) / withReturn.length
                                : null

                        return (
                            <div key={manager.id} className="asset-mgmt__manager-group">
                                <div className="asset-mgmt__manager-header">
                                    <div className="asset-mgmt__mgr-avatar">{manager.initials}</div>
                                    <div className="asset-mgmt__mgr-info">
                                        <div className="asset-mgmt__mgr-name">{manager.nickname}</div>
                                        <div className="asset-mgmt__mgr-meta">
                                            {manager.style} · {group.length} 个组合 · 管理 {fmtWan(group.reduce((s, p) => s + (p.current_nav ?? 0), 0))}
                                        </div>
                                    </div>
                                    <div className="asset-mgmt__mgr-stats">
                                        <div className="asset-mgmt__mgr-stat">
                                            <span className={`asset-mgmt__mgr-stat-val ${retClass(groupTdPl)}`}>
                                                {fmtSignedWan(groupTdPl)}
                                            </span>
                                            <span className="asset-mgmt__mgr-stat-lbl">今日 P&L</span>
                                        </div>
                                        <div className="asset-mgmt__mgr-stat">
                                            <span className={`asset-mgmt__mgr-stat-val ${retClass(avg('daily_return_pct'))}`}>
                                                {fmtPct(avg('daily_return_pct'))}
                                            </span>
                                            <span className="asset-mgmt__mgr-stat-lbl">今日</span>
                                        </div>
                                        <div className="asset-mgmt__mgr-stat">
                                            <span className={`asset-mgmt__mgr-stat-val ${retClass(avg('cumulative_return_pct'))}`}>
                                                {fmtPct(avg('cumulative_return_pct'))}
                                            </span>
                                            <span className="asset-mgmt__mgr-stat-lbl">累计</span>
                                        </div>
                                    </div>
                                </div>

                                {viewMode === 1 ? (
                                    <div className="asset-mgmt__card-row">
                                        {group.map(p => (
                                            <PortfolioCard key={p.id} p={p} onDetail={id => navigate(`/asset-mgmt/${id}`)} />
                                        ))}
                                    </div>
                                ) : (
                                    <Table
                                        dataSource={group}
                                        columns={columns}
                                        rowKey="id"
                                        pagination={false}
                                        size="small"
                                        onRow={r => ({ onClick: () => navigate(`/asset-mgmt/${r.id}`) })}
                                        style={{ cursor: 'pointer' }}
                                    />
                                )}
                            </div>
                        )
                    })
                )}
            </div>
        </div>
    )
}
